import React, { useEffect, useState } from "react";
import { Card, Row, Col, Button, Skeleton } from "antd";
import { LoginOutlined, CheckCircleFilled } from "@ant-design/icons";
import dayjs from "dayjs";
import { MemberCheckinEntry } from "../../../../model/members/MemberCheckinEntry";
import { useMemberService } from "../../../../services/members/MemberServiceProvider";
import { MemberProfileMode } from "./modeConfig";

interface CheckinSummaryProps {
    membershipId: string;
    mode: MemberProfileMode;
    onCheckIn: () => void;
    checkingIn: boolean;
    checkInDisabled: boolean;
    // Bumped by the parent after a check-in so the tiles refresh.
    refreshKey?: number;
    // Set when a check-in was performed in this session (a manual click or the
    // card-scan ?checkin=true flow). When set, the most-recent entry IS that
    // just-now check-in, so the "last check-in" tile shows the visit BEFORE it.
    checkedInAt?: Date | null;
}

const fmtDateTime = (d: Date) => dayjs(d).format("D MMM YYYY, HH:mm");

const daysAgoLabel = (d: Date): string => {
    const diff = dayjs().startOf("day").diff(dayjs(d).startOf("day"), "day");
    if (diff <= 0) return "Today";
    if (diff === 1) return "Yesterday";
    return `${diff} days ago`;
};

// Two stat tiles for the left rail: last check-in (date/time + how long ago) and
// check-ins in the last 30 days, with an optional check-in CTA. When the member
// was just checked in this session, the tile shows their previous visit instead
// of the check-in that just happened.
const CheckinSummary: React.FC<CheckinSummaryProps> = ({
    membershipId,
    mode,
    onCheckIn,
    checkingIn,
    checkInDisabled,
    refreshKey = 0,
    checkedInAt = null,
}) => {
    const memberService = useMemberService();
    const [loading, setLoading] = useState(true);
    const [checkins, setCheckins] = useState<MemberCheckinEntry[]>([]);

    useEffect(() => {
        let cancelled = false;
        const fetch = async () => {
            setLoading(true);
            try {
                const history = await memberService.getCheckinHistory(membershipId);
                if (!cancelled) setCheckins(history ?? []);
            } catch (error) {
                console.error("Error fetching checkin history:", error);
                if (!cancelled) setCheckins([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        if (membershipId) fetch();
        return () => {
            cancelled = true;
        };
    }, [membershipId, memberService, refreshKey]);

    const justCheckedIn = !!checkedInAt;
    const sorted = [...checkins].sort((a, b) => new Date(b.checkInUtc).getTime() - new Date(a.checkInUtc).getTime());
    // When we just checked them in, skip that most-recent entry and show the
    // prior visit; otherwise show the most recent.
    const lastVisit = (justCheckedIn ? sorted[1] : sorted[0]) ?? null;
    const thirtyDaysAgo = dayjs().subtract(30, "days");
    const last30 = checkins.filter((c) => dayjs(c.checkInUtc).isAfter(thirtyDaysAgo)).length;

    return (
        <Card
            title="Activity"
            size="small"
            styles={{ body: { padding: 16 } }}
            extra={
                mode.checkInButton && !justCheckedIn ? (
                    <Button type="primary" size="small" icon={<LoginOutlined />} loading={checkingIn} disabled={checkInDisabled} onClick={onCheckIn}>
                        Check in
                    </Button>
                ) : null
            }
        >
            {loading ? (
                <Skeleton active paragraph={{ rows: 2 }} />
            ) : (
                <>
                    {justCheckedIn && (
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                                marginBottom: 12,
                                color: "var(--ft-success)",
                                fontWeight: 600,
                                fontSize: 13,
                            }}
                        >
                            <CheckCircleFilled />
                            Checked in just now{checkedInAt ? ` · ${dayjs(checkedInAt).format("HH:mm")}` : ""}
                        </div>
                    )}
                    <Row gutter={[12, 12]}>
                        <Col span={12}>
                            <div className="ft-stat">
                                <div className="lbl">{justCheckedIn ? "Previous check-in" : "Last check-in"}</div>
                                {lastVisit ? (
                                    <>
                                        <div className="num" style={{ fontSize: 17, color: "var(--ft-success)" }}>
                                            <CheckCircleFilled style={{ fontSize: 14, marginRight: 6 }} />
                                            {daysAgoLabel(lastVisit.checkInUtc)}
                                        </div>
                                        <div style={{ fontSize: 12, color: "var(--ft-muted)", marginTop: 2 }}>
                                            {fmtDateTime(lastVisit.checkInUtc)}
                                        </div>
                                    </>
                                ) : (
                                    <div className="num" style={{ fontSize: 17, color: "var(--ft-muted)" }}>
                                        {justCheckedIn ? "No previous visits" : "Never"}
                                    </div>
                                )}
                            </div>
                        </Col>
                        <Col span={12}>
                            <div className="ft-stat">
                                <div className="lbl">Check-ins · 30 days</div>
                                <div className="num">{last30}</div>
                            </div>
                        </Col>
                    </Row>
                </>
            )}
        </Card>
    );
};

export default CheckinSummary;
